<template>
	<div>
		<DropdownStatus
			v-model="currentLng"
			:options="props.languages"
			optionLabel="title"
			optionValue="identifier"
			@change="switchLanguage"
			placeholder="'Select Language'"
			class="w-full shadow-none border h-full items-center"
		>
			<template #value>
				<div class="flex items-center text-md">
					<span :class="`fi fi-${flagCode(selectedLanguage?.identifier)}`"></span>
					<div class="ml-2" v-if="!props.hideLabel">{{ selectedLanguage?.title }}</div>
				</div>
			</template>

			<template #option="slotProps">
				<div class="flex items-center text-md">
					<span :class="`fi fi-${flagCode(slotProps.option.identifier)}`"></span>
					<div class="ml-2" v-if="!props.hideLabel">{{ slotProps.option.title }}</div>
				</div>
			</template>
		</DropdownStatus>
	</div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import type { LanguageItem } from '@/types';
import { useAppStore } from '@/stores/app-store';
import { useRoute } from 'vue-router';

const { currentLng, syncLngFromSystem } = useAppStore();
const route = useRoute();
const emit = defineEmits<{ changeLanguage: [string] }>();


const props = withDefaults( defineProps<{
	languages: LanguageItem[];
	hideLabel?: boolean;
	hideHeader?: boolean;

}>(), {
	hideLabel: false,
	hideHeader: true,
} )

const selectedIdentifierFromPayload = computed(() => {
	for (const item of props.languages) {
		const rawSelected = String(item.selected ?? '').trim()
		if (!rawSelected) {
			continue
		}
		const normalizedSelected = rawSelected.toLowerCase()
		const normalizedIdentifier = String(item.identifier ?? '').trim().toLowerCase()
		if (
			normalizedSelected === normalizedIdentifier ||
			normalizedSelected === 'y' ||
			normalizedSelected === 'yes' ||
			normalizedSelected === 'true' ||
			normalizedSelected === '1'
		) {
			return item.identifier
		}
	}
	return null
})

watch(
	() => selectedIdentifierFromPayload.value,
	(selectedIdentifier) => {
		const routeLng = route.query.lng
		const hasRouteLng = typeof routeLng === 'string'
			? routeLng.trim().length > 0
			: Array.isArray(routeLng)
				? routeLng.some((entry) => typeof entry === 'string' && entry.trim().length > 0)
				: false
		if (!hasRouteLng && selectedIdentifier && selectedIdentifier !== currentLng.value) {
			syncLngFromSystem(selectedIdentifier)
		}
	},
	{ immediate: true }
)

const selectedLanguage = computed(() =>
	props.languages.find((item) => item.identifier === currentLng.value) ??
	props.languages.find((item) => item.identifier === selectedIdentifierFromPayload.value) ??
	props.languages[0]
);


const flagCode = (code?: string) => {
	if (!code) return 'xx';
	if (code === 'sq') return 'al';
	if (code === 'en') return 'gb';
	return code;
};

const switchLanguage = () => {
	if (selectedLanguage.value) {
		emit('changeLanguage', selectedLanguage.value.identifier);
	}
};
</script>
